import { makeContext } from "@core/utils/makeContext";
import { logger } from "../utils/chimeInit";
import { VideoRoomAccessResponse } from "../utils/api/verifyVideoRoomAccess";
import { RoomMember } from "../types/video.types";
import { MeetingDetails } from "../utils/api/getMeetingDetails";

// --- Name ---
const NAME = "Video";

// --- Types ---
export type AttendeeInfo = {
  attendeeId: string;
  externalUserId?: string;
  name: string;
  isLocal: boolean;
};

export type VideoState = {
  meetingId: string | null;
  roomId: number | null;
  isInitialized: boolean;
  roomAccess: VideoRoomAccessResponse | null;
  isVerifyingRoomAccess: boolean;
  roomAccessError: string | null;
  lastHeartbeat: string | null;
  userName: string | null;
  roster: Record<string, AttendeeInfo>;
  activeSpeakers: string[]; // Array of attendee IDs who are currently speaking
  roomMembersData: Record<number, RoomMember>; // Map of user_id to RoomMember
  meetingDetails: MeetingDetails | null;
  isFetchingMeetingDetails: boolean;
  meetingDetailsError: string | null;
  ccStatus: 'on' | 'off'; // Closed caption/transcription status from heartbeat
};

export type VideoAction = {
  type: string;
  payload?:
    | Partial<VideoState>
    | { attendeeId: string; attendeeInfo: AttendeeInfo }
    | { attendeeId: string }
    | { attendeeId: string; isSpeaking: boolean }
    | { roomMembersData: RoomMember[] }
    | { meetingDetails: MeetingDetails }
    | { meetingDetailsError: string };
};

// --- Constants ---
export const SET_MEETING_ID = "video/set/meetingId" as const;
export const SET_ROOM_ID = "video/set/roomId" as const;
export const CLEAR_MEETING_ID = "video/clear/meetingId" as const;
export const CLEAR_ROOM_ID = "video/clear/roomId" as const;
export const SET_INITIALIZED = "video/set/initialized" as const;
export const SET_ROOM_ACCESS = "video/set/roomAccess" as const;
export const SET_VERIFYING_ROOM_ACCESS =
  "video/set/verifyingRoomAccess" as const;
export const SET_ROOM_ACCESS_ERROR = "video/set/roomAccessError" as const;
export const SET_USER_NAME = "video/set/userName" as const;
export const SET_ROOM_MEMBERS_DATA = "video/set/roomMembersData" as const;
export const ADD_ACTIVE_SPEAKER = "video/add/activeSpeaker" as const;
export const REMOVE_ACTIVE_SPEAKER = "video/remove/activeSpeaker" as const;
export const CLEAR_ACTIVE_SPEAKERS = "video/clear/activeSpeakers" as const;
export const SET_MEETING_DETAILS = "video/set/meetingDetails" as const;
export const SET_FETCHING_MEETING_DETAILS =
  "video/set/fetchingMeetingDetails" as const;
export const SET_MEETING_DETAILS_ERROR =
  "video/set/meetingDetailsError" as const;
export const SET_CC_STATUS = "video/set/ccStatus" as const;

export const ACTION_TYPES = {
  SET_MEETING_ID,
  SET_ROOM_ID,
  CLEAR_MEETING_ID,
  CLEAR_ROOM_ID,
  SET_INITIALIZED,
  SET_ROOM_ACCESS,
  SET_VERIFYING_ROOM_ACCESS,
  SET_ROOM_ACCESS_ERROR,
  SET_USER_NAME,
  SET_ROOM_MEMBERS_DATA,
  ADD_ACTIVE_SPEAKER,
  REMOVE_ACTIVE_SPEAKER,
  CLEAR_ACTIVE_SPEAKERS,
  SET_MEETING_DETAILS,
  SET_FETCHING_MEETING_DETAILS,
  SET_MEETING_DETAILS_ERROR,
} as const;

// --- Initial State ---
export const initialVideo: VideoState = {
  meetingId: null,
  roomId: null,
  isInitialized: false,
  roomAccess: null,
  isVerifyingRoomAccess: false,
  roomAccessError: null,
  lastHeartbeat: null,
  userName: null,
  roster: {},
  activeSpeakers: [],
  roomMembersData: {},
  meetingDetails: null,
  isFetchingMeetingDetails: false,
  meetingDetailsError: null,
  ccStatus: 'off',
};

// --- Utility Functions ---
export const generateUniqueUserName = (
  firstName: string,
  lastName: string,
  userId: number
): string => {
  const timestamp = Math.floor(Date.now() / 1000); // Unix timestamp
  return `${firstName}-${lastName}-TS-${timestamp}-UID-${userId}`;
};

// --- Reducer ---
function videoReducer(state: VideoState, action: VideoAction): VideoState {
  switch (action.type) {
    case SET_MEETING_ID:
      if (action.payload && "meetingId" in action.payload) {
        return {
          ...state,
          meetingId: action.payload.meetingId ?? null,
        };
      }
      return state;
    case SET_ROOM_ID:
      if (action.payload && "roomId" in action.payload) {
        return {
          ...state,
          roomId: action.payload.roomId ?? null,
        };
      }
      return state;
    case CLEAR_MEETING_ID:
      return {
        ...state,
        meetingId: null,
        isInitialized: false,
      };
    case CLEAR_ROOM_ID:
      return {
        ...state,
        roomId: null,
      };
    case SET_INITIALIZED:
      if (action.payload && "isInitialized" in action.payload) {
        return {
          ...state,
          isInitialized: action.payload.isInitialized ?? false,
        };
      }
      return state;
    case SET_ROOM_ACCESS:
      if (action.payload && "roomAccess" in action.payload) {
        return {
          ...state,
          roomAccess: action.payload.roomAccess ?? null,
        };
      }
      return state;
    case SET_VERIFYING_ROOM_ACCESS:
      if (action.payload && "isVerifyingRoomAccess" in action.payload) {
        return {
          ...state,
          isVerifyingRoomAccess: action.payload.isVerifyingRoomAccess ?? false,
        };
      }
      return state;
    case SET_ROOM_ACCESS_ERROR:
      if (action.payload && "roomAccessError" in action.payload) {
        return {
          ...state,
          roomAccessError: action.payload.roomAccessError ?? null,
          lastHeartbeat: action.payload.lastHeartbeat ?? null,
        };
      }
      return state;
    case SET_USER_NAME:
      if (action.payload && "userName" in action.payload) {
        return {
          ...state,
          userName: action.payload.userName ?? null,
        };
      }
      return state;
    case SET_ROOM_MEMBERS_DATA:
      if (
        action.payload &&
        "roomMembersData" in action.payload &&
        Array.isArray(action.payload.roomMembersData)
      ) {
        const roomMembersData = action.payload.roomMembersData;
        const roomMembersDataMap: Record<number, RoomMember> = {};

        roomMembersData.forEach((member) => {
          roomMembersDataMap[member.user_id] = member;
        });

        return {
          ...state,
          roomMembersData: roomMembersDataMap,
        };
      }
      return state;
    case ADD_ACTIVE_SPEAKER:
      if (action.payload && "attendeeId" in action.payload) {
        const { attendeeId } = action.payload;
        if (!state.activeSpeakers.includes(attendeeId)) {
          const newActiveSpeakers = [...state.activeSpeakers, attendeeId];

          return {
            ...state,
            activeSpeakers: newActiveSpeakers,
          };
        }
      }
      return state;
    case REMOVE_ACTIVE_SPEAKER:
      if (action.payload && "attendeeId" in action.payload) {
        const { attendeeId } = action.payload;
        const newActiveSpeakers = state.activeSpeakers.filter(
          (id) => id !== attendeeId
        );

        return {
          ...state,
          activeSpeakers: newActiveSpeakers,
        };
      }
      return state;
    case CLEAR_ACTIVE_SPEAKERS:
      return {
        ...state,
        activeSpeakers: [],
      };
    case SET_MEETING_DETAILS:
      if (action.payload && "meetingDetails" in action.payload) {
        return {
          ...state,
          meetingDetails: action.payload.meetingDetails ?? null,
        };
      }
      return state;
    case SET_FETCHING_MEETING_DETAILS:
      if (action.payload && "isFetchingMeetingDetails" in action.payload) {
        return {
          ...state,
          isFetchingMeetingDetails:
            action.payload.isFetchingMeetingDetails ?? false,
        };
      }
      return state;
    case SET_MEETING_DETAILS_ERROR:
      if (action.payload && "meetingDetailsError" in action.payload) {
        return {
          ...state,
          meetingDetailsError: action.payload.meetingDetailsError ?? null,
        };
      }
      return state;
    case SET_CC_STATUS:
      if (action.payload && "ccStatus" in action.payload) {
        return {
          ...state,
          ccStatus: action.payload.ccStatus ?? 'off',
        };
      }
      return state;
    default:
      throw new Error("Unknown action: " + action.type);
  }
}

// --- Context Creation ---
export const {
  Provider: VideoProvider,
  useState: useVideo,
  useDispatch: useVideoDispatch,
} = makeContext<VideoState, VideoAction>({
  name: NAME,
  initialState: initialVideo,
  reducer: videoReducer,
});
